import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Loader2, AlertCircle, Car, Calendar, CreditCard,
  Mail, Phone, ShieldCheck, CheckCircle2, XCircle, Clock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (isoString) => {
  if (!isoString) return '-';
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return isoString;
  }
};

const statusConfig = {
  active: { label: 'Actif', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle2 },
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock },
  cancelled: { label: 'Résilié', color: 'bg-gray-100 text-gray-800 border-gray-200', icon: XCircle },
  past_due: { label: 'Impayé', color: 'bg-red-100 text-red-800 border-red-200', icon: AlertCircle },
};

const SubscriptionPortalPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    const loadPortal = async () => {
      if (!token) {
        setError("Lien d'accès invalide. Veuillez demander un nouveau lien.");
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(`${API}/api/customer/portal?token=${encodeURIComponent(token)}`);
        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.detail || "Impossible de charger votre abonnement");
        }
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadPortal();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-16 px-4 flex items-center justify-center" data-testid="portal-loading">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#52a675] mx-auto mb-4" />
          <p className="text-gray-600">Chargement de votre abonnement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-32 pb-16 px-4 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto max-w-xl">
          <Card className="border-0 shadow-xl" data-testid="portal-error-card">
            <CardContent className="pt-10 pb-10 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-5">
                <AlertCircle className="w-9 h-9 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Lien invalide</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <Link to="/mon-abonnement">
                <Button className="bg-[#52a675] hover:bg-[#3d8a5c] text-white" data-testid="portal-request-new-link-button">
                  Demander un nouveau lien
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const { customer, subscriptions } = data;
  const customerDisplayName = customer.is_company && customer.company_name
    ? customer.company_name
    : `${customer.first_name || ''} ${customer.last_name || ''}`.trim();

  return (
    <div className="min-h-screen pt-32 pb-16 px-4 bg-gradient-to-br from-gray-50 to-gray-100" data-testid="subscription-portal-page">
      <div className="container mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-[#52a675]/10 flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-[#52a675]" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">Mon abonnement</h1>
              <p className="text-gray-600 text-sm">Bonjour <span className="font-medium">{customerDisplayName}</span></p>
            </div>
          </div>
        </div>

        {/* Subscriptions list */}
        {subscriptions.length === 0 ? (
          <Card className="border-0 shadow-md" data-testid="portal-no-subscription">
            <CardContent className="pt-10 pb-10 text-center">
              <p className="text-gray-600">Aucun abonnement trouvé pour ce compte.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {subscriptions.map((sub) => {
              const status = statusConfig[sub.status] || statusConfig.pending;
              const StatusIcon = status.icon;
              return (
                <Card key={sub.id} className="border-0 shadow-lg overflow-hidden" data-testid={`subscription-card-${sub.id}`}>
                  <CardHeader className="bg-gradient-to-r from-[#52a675] to-[#3d8a5c] text-white pb-4">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <CardTitle className="text-2xl font-bold mb-1">
                          Abonnement Plan {sub.plan_code}
                        </CardTitle>
                        <p className="text-white/90 text-sm">
                          CHF {sub.monthly_price?.toFixed(2)} / mois - Engagement {sub.commitment_months} mois
                        </p>
                      </div>
                      <Badge className={`${status.color} border px-3 py-1 flex items-center gap-1.5`} data-testid={`subscription-status-${sub.id}`}>
                        <StatusIcon className="w-3.5 h-3.5" />
                        {status.label}
                      </Badge>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-6 space-y-6">
                    {/* Vehicle */}
                    {sub.vehicle && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                          <Car className="w-4 h-4" /> Véhicule
                        </h3>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <InfoRow label="Marque" value={sub.vehicle.brand} />
                          <InfoRow label="Modèle" value={sub.vehicle.model} />
                          <InfoRow label="Année" value={sub.vehicle.year} />
                          {sub.vehicle.plate_number && <InfoRow label="Plaque" value={sub.vehicle.plate_number} />}
                        </div>
                      </div>
                    )}

                    {/* Billing */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <CreditCard className="w-4 h-4" /> Facturation
                      </h3>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <InfoRow label="Prix mensuel" value={`CHF ${sub.monthly_price?.toFixed(2)}`} />
                        <InfoRow label="Franchise" value={`CHF ${sub.franchise_chf}`} />
                        {sub.stripe?.next_billing_date && (
                          <InfoRow label="Prochain prélèvement" value={sub.stripe.next_billing_date} highlight />
                        )}
                        {sub.stripe?.cancel_at_period_end && (
                          <InfoRow label="Statut" value="Sera résilié à la fin de la période" highlight />
                        )}
                      </div>
                    </div>

                    {/* Commitment */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Calendar className="w-4 h-4" /> Engagement
                      </h3>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <InfoRow
                          label={sub.start_date_is_estimated ? "Date de début (estimée)" : "Date de début"}
                          value={formatDate(sub.start_date)}
                        />
                        <InfoRow label="Fin d'engagement" value={sub.commitment_end_date || '-'} />
                      </div>
                      {sub.start_date_is_estimated && (
                        <p className="text-xs text-gray-500 mt-2 italic">
                          La date précise sera mise à jour lors de l'activation Stripe.
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Contact info for cancellation */}
        <Card className="mt-8 border-0 shadow-md bg-white" data-testid="portal-contact-info">
          <CardContent className="pt-6 pb-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  Besoin de résilier ou modifier votre contrat ?
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  La résiliation n'est pas disponible en ligne. Contactez notre équipe pour toute demande
                  de résiliation ou de modification de votre abonnement.
                </p>
                <div className="flex flex-wrap gap-3">
                  <a href="mailto:info@car-main.ch">
                    <Button variant="outline" size="sm" className="gap-2" data-testid="portal-contact-email-button">
                      <Mail className="w-4 h-4" />
                      info@car-main.ch
                    </Button>
                  </a>
                  <Link to="/contact">
                    <Button variant="outline" size="sm" className="gap-2" data-testid="portal-contact-form-button">
                      <Phone className="w-4 h-4" />
                      Formulaire de contact
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const InfoRow = ({ label, value, highlight }) => (
  <div className={`p-3 rounded-lg ${highlight ? 'bg-[#52a675]/5 border border-[#52a675]/20' : 'bg-gray-50'}`}>
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className={`font-medium ${highlight ? 'text-[#52a675]' : 'text-gray-900'}`}>{value || '-'}</p>
  </div>
);

export default SubscriptionPortalPage;
